import { mobileSearchInputStyles } from "@components/layouts/navigation/styles";
import { SEARCHBAR_INPUT } from "@e2e/helpers/homepage/constants";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { IconButton } from "@ui/components/forms/icon-button/IconButton";
import { Input } from "@ui/components/forms/input/Input";
import { InputLeftElement } from "@ui/components/forms/input/InputElement";
import { InputGroup } from "@ui/components/forms/input/InputGroup";
import type { BoxProps } from "@ui/components/layout/box/Box";
import { Box } from "@ui/components/layout/box/Box";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { useThemeMediaQuery } from "@ui/hooks/useThemeMediaQuery";
import { AnimatePresence, motion } from "framer-motion";
import type { Dispatch, FC, SetStateAction } from "react";
import { useEffect, useMemo, useRef } from "react";
import { sizes } from "ui/src/design-tokens";
import { AutoCompletePanel } from "../components/AutoCompletePanel";
import { useSearchBar } from "./useSearchBar";

interface SearchBarProps extends BoxProps {
    onOpen?: () => void;
    searchBarOpen?: boolean;
    setSearchBarOpen?: Dispatch<SetStateAction<boolean>>;
}

export const searchBarWrapper = "search-bar-wrapper";

export const SearchBar: FC<SearchBarProps> = ({ searchBarOpen, setSearchBarOpen }) => {
    const { get: getMicrocopy } = useMicrocopy();
    const { xl } = useThemeMediaQuery();
    const isDesktopView = xl;

    const {
        autocomplete,
        searchPagePath,
        recentSearchesPlugin,
        containerRef,
        formRef,
        inputRef,
        isLoading,
        setIsLoading,
        autocompleteState,
    } = useSearchBar({ searchBarOpen, isDesktopView });

    const closeSearch = useRef();
    const isTabbing = useRef(false);

    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }
    }, [containerRef]);

    useEffect(() => {
        searchBarOpen && inputRef && inputRef.current.focus();
    }, [inputRef, searchBarOpen]);

    const formProps = useMemo(
        () =>
            autocomplete?.getFormProps({
                inputElement: inputRef.current,
            }),
        [autocomplete, inputRef]
    );
    const inputProps = useMemo(
        () =>
            autocomplete?.getInputProps({
                inputElement: inputRef?.current,
                maxLength: 100,
                value: autocompleteState?.query,
            }),
        [autocomplete, inputRef, autocompleteState]
    );

    const autocompleteStyles = {
        left: searchBarOpen ? 0 : "inherit",
        // The number of pixels is the logo container width (129px) - paddings on the side (30px * 2) - scrollbar (15px...
        // This value might change, but will be more "cheap" to have a fixed value instead a calculation to get the scrollbar width) = 204px
        // Mobile: width of close button (52px) + marginRight (30px)
        width: searchBarOpen ? (isDesktopView ? "calc(100vw - 204px)" : "calc(100vw - 82px)") : 0,
        zIndex: searchBarOpen ? 1 : 0,
        originX: 0,
        originY: "100%",
        originZ: 0,
    };

    useEffect(() => {
        let analyticsTimeout;
        if (autocompleteState?.query) {
            analyticsTimeout = setTimeout(() => {
                AnalyticsClient.saveSearch(autocompleteState?.query);
            }, 300);
        }
        return () => clearTimeout(analyticsTimeout);
    }, [autocompleteState?.query]);

    return (
        <>
            <AnimatePresence>
                <motion.div
                    tabIndex={0}
                    className="aa-Autocomplete"
                    ref={containerRef}
                    layout
                    {...autocomplete?.getRootProps()}
                    initial={false}
                    animate={autocompleteStyles}
                    transition={{
                        duration: 0.4,
                        type: "tween",
                    }}
                    exit={{
                        left: "inherit",
                        width: searchBarOpen ? "calc(100vw - 202px)" : 0,
                        opacity: 0,
                        zIndex: 0,
                    }}
                    style={{
                        position: "absolute",
                        transformOrigin: "center",
                        width: "100%",
                    }}
                    data-testid={searchBarWrapper}
                >
                    <form
                        ref={formRef}
                        className="aa-Form"
                        {...(formProps as any)}
                        key="search-form"
                    >
                        <InputGroup
                            sx={{
                                opacity: searchBarOpen ? 1 : 0,
                            }}
                            className="aa-InputWrapper"
                            key="aa-InputWrapper"
                            w="100%"
                            bg="white"
                            visibility={searchBarOpen ? "visible" : "hidden"}
                        >
                            <InputLeftElement
                                w={sizes.icon["4xs"]}
                                h={sizes.navBarCollapsedHeight}
                                zIndex={3}
                                position="absolute"
                                tabIndex={-1}
                            >
                                <ECCOIcon name="search" aria-label="Search" />
                            </InputLeftElement>
                            <Input
                                tabIndex={0}
                                className="aa-Input"
                                ref={inputRef}
                                aria-selected={searchBarOpen}
                                display={[
                                    searchBarOpen ? "flex" : "none",
                                    searchBarOpen ? "flex" : "none",
                                    "flex",
                                    "flex",
                                ]}
                                {...(inputProps as any)}
                                sx={mobileSearchInputStyles}
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                                data-testid={SEARCHBAR_INPUT}
                                onKeyUp={(e) => {
                                    if (e.key === "Tab" && isTabbing.current) {
                                        if (e.shiftKey) {
                                            isTabbing.current = false;
                                        }
                                        isTabbing.current = false;
                                        if (closeSearch?.current) {
                                            //@ts-ignore
                                            closeSearch?.current?.focus();
                                        }
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Escape") {
                                        setSearchBarOpen(false);
                                        return;
                                    }
                                    if (e.key === "Tab") {
                                        if (e.shiftKey) {
                                            isTabbing.current = false;
                                            setSearchBarOpen(false);
                                            setTimeout(() => {
                                                const elements = document.querySelectorAll(
                                                    'div[data-testid="navPreSearchBox"] > div[data-testid="DesktopNavItem"] > a'
                                                );
                                                const el = elements.length
                                                    ? elements[elements.length - 1]
                                                    : null;
                                                if (el) {
                                                    // @ts-ignore
                                                    el.focus();
                                                }
                                                setSearchBarOpen(false);
                                            }, 50);
                                        } else {
                                            isTabbing.current = true;
                                        }
                                    }
                                }}
                            />
                        </InputGroup>
                    </form>
                    {searchBarOpen && (
                        <Box
                            width="10.4"
                            display={{ base: "none", xl: "flex" }}
                            h={sizes.navBarCollapsedHeight}
                            justifyContent="center"
                            zIndex={2}
                            position="absolute"
                            right={0}
                            top={0}
                        >
                            <IconButton
                                ref={closeSearch}
                                tabIndex={0}
                                w="10.4"
                                height={[, "100%"]}
                                zIndex={3}
                                borderRadius="0"
                                bg="white"
                                display={{ base: "none", xl: "flex" }}
                                aria-label={getMicrocopy(
                                    CommonCMS.shared,
                                    CommonCMS.ariaLabels.closeModalAria
                                )}
                                icon={<ECCOIcon name="close" />}
                                variant="tertiary"
                                onKeyDown={(e) => {
                                    if (
                                        e.key === "Space" ||
                                        e.key === "Escape" ||
                                        e.key === "Enter"
                                    ) {
                                        setSearchBarOpen(false);
                                    }
                                }}
                                onClick={() => {
                                    setTimeout(() => setSearchBarOpen(false), 18);
                                }}
                            />
                        </Box>
                    )}
                </motion.div>
            </AnimatePresence>
            <AutoCompletePanel
                autocompleteState={autocompleteState}
                autocomplete={autocomplete}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                searchPagePath={searchPagePath}
                recentSearchesPlugin={recentSearchesPlugin}
                searchBarOpen={searchBarOpen}
            />
        </>
    );
};
